import { useState, useCallback } from 'react';
import { rooms } from './rooms';
import { initializeMissingPuzzleStates } from './engine';
import type { EscapeStateV2, PuzzleInstanceState } from './types';

const STORAGE_KEY = 'nob_escape_v2';

function buildFreshState(): EscapeStateV2 {
  return {
    started: false,
    currentRoomIndex: 0,
    completedRooms: [false, false, false],
    finished: false,
    inventory: [],
    puzzleState: initializeMissingPuzzleStates(rooms, {}),
    hintsRevealed: {},
  };
}

function loadState(): EscapeStateV2 {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as EscapeStateV2;
      parsed.puzzleState = initializeMissingPuzzleStates(rooms, parsed.puzzleState ?? {});
      return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return buildFreshState();
}

function persist(state: EscapeStateV2) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

export function useEscapeState() {
  const [state, setState] = useState<EscapeStateV2>(loadState);

  function update(updater: (prev: EscapeStateV2) => EscapeStateV2) {
    setState(prev => {
      const next = updater(prev);
      persist(next);
      return next;
    });
  }

  const begin = useCallback(() => {
    update(s => ({ ...s, started: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = useCallback(() => {
    update(s => ({ ...s, finished: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    const fresh = buildFreshState();
    persist(fresh);
    setState(fresh);
  }, []);

  const advanceRoom = useCallback(() => {
    update(s => ({
      ...s,
      currentRoomIndex: Math.min(s.currentRoomIndex + 1, 2),
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRoomComplete = useCallback((roomIndex: number) => {
    update(s => {
      const completedRooms = [...s.completedRooms] as [boolean, boolean, boolean];
      completedRooms[roomIndex] = true;
      return { ...s, completedRooms };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function patchPuzzle<T extends PuzzleInstanceState>(
    puzzleId: string,
    updater: (prev: T) => T,
  ) {
    update(s => ({
      ...s,
      puzzleState: {
        ...s.puzzleState,
        [puzzleId]: updater(s.puzzleState[puzzleId] as T),
      },
    }));
  }

  const grantItem = useCallback((itemId: string) => {
    update(s => {
      if (s.inventory.includes(itemId)) return s;
      return { ...s, inventory: [...s.inventory, itemId] };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const revealNextHint = useCallback((puzzleId: string) => {
    update(s => {
      const current = s.hintsRevealed[puzzleId] ?? 0;
      if (current >= 3) return s;
      return { ...s, hintsRevealed: { ...s.hintsRevealed, [puzzleId]: current + 1 } };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    state,
    begin,
    finish,
    reset,
    advanceRoom,
    markRoomComplete,
    patchPuzzle,
    grantItem,
    revealNextHint,
  };
}
